import { BOARD_COLS, type Board, type WidgetInstance } from "@kourai/api/home-board";
import type { LayoutItem } from "react-grid-layout";
import { verticalCompactor } from "react-grid-layout/core";
import { fromGrid, toGrid } from "./layout";

/**
 * Replays local edits on top of a newer board from the server (ADMIN-259), widget by widget, so
 * two devices editing at once lose nothing silently.
 *
 * `base` is the board both sides started from (what this device last saved or loaded), `local`
 * is this device's board now, `server` is what another device saved since. For each widget:
 *
 * - changed here (moved, resized, configured) or added here: this device's version wins,
 * - removed here: it stays removed,
 * - untouched here: the server's version (moved, changed or removed there) wins,
 * - removed there: gone, even if this device moved it (a removal is deliberate, a nudge isn't),
 * - added on the server: kept.
 *
 * Then overlaps are settled the way the grid does (vertical compaction), with this device's
 * edited widgets placed first so they keep their spots.
 */
export function rebase(base: Board, local: Board, server: Board): Board {
  const inBase = new Map(base.items.map((i) => [i.id, i]));
  const inLocal = new Map(local.items.map((i) => [i.id, i]));
  const changedHere = new Set<string>();
  const items: WidgetInstance[] = [];

  for (const s of server.items) {
    const b = inBase.get(s.id);
    const l = inLocal.get(s.id);
    if (!b)
      items.push(s); // added there (or here and there under the same id: theirs)
    else if (!l)
      continue; // removed here
    else if (same(b, l))
      items.push(s); // untouched here
    else {
      items.push(l);
      changedHere.add(l.id);
    }
  }
  for (const l of local.items) {
    if (inBase.has(l.id) || items.some((i) => i.id === l.id)) continue;
    items.push(l); // added here
    changedHere.add(l.id);
  }

  const merged: Board = { ...server, items };
  const grid = toGrid(merged);
  const ordered: LayoutItem[] = [
    ...grid.filter((g) => changedHere.has(g.i)).map((g) => ({ ...g, static: true })),
    ...grid.filter((g) => !changedHere.has(g.i)),
  ];
  // Held tiles first push the others down, then everything settles up without the hold.
  const settled = verticalCompactor
    .compact(ordered, BOARD_COLS)
    .map(({ static: _held, ...g }) => g as LayoutItem);
  return fromGrid(merged, verticalCompactor.compact(settled, BOARD_COLS));
}

function same(a: WidgetInstance, b: WidgetInstance) {
  return (
    a.x === b.x &&
    a.y === b.y &&
    a.w === b.w &&
    a.h === b.h &&
    a.typeVersion === b.typeVersion &&
    JSON.stringify(a.config) === JSON.stringify(b.config)
  );
}
